import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { authService } from '../services/authService';
import api from '../../../services/api';

interface User {
  id: string;
  email: string;
  name: string;
  picture: string;
  status: 'pending' | 'approved' | 'blocked';
  role: 'user' | 'admin';
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credential: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SK_TOKEN = 'fin_token';
const SK_USER = 'fin_user';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem(SK_TOKEN);
    localStorage.removeItem(SK_USER);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      if (res.data.success) {
        const updated = res.data.data;
        setUser(updated);
        localStorage.setItem(SK_USER, JSON.stringify(updated));
      }
    } catch { /* ignore */ }
  }, []);

  const login = useCallback(async (credential: string): Promise<void> => {
    try {
      setIsLoading(true);
      const res = await authService.loginWithGoogle(credential);
      const data = res.data;
      if (!data.success || !data.data) {
        throw new Error(data.error || 'Falha na autenticação');
      }
      setUser(data.data.user);
      setToken(data.data.token);
      localStorage.setItem(SK_TOKEN, data.data.token);
      localStorage.setItem(SK_USER, JSON.stringify(data.data.user));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem(SK_TOKEN);
    const storedUser = localStorage.getItem(SK_USER);
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setIsLoading(false);
  }, []);

  // Polling: se user está pending, verifica a cada 30s se foi aprovado
  useEffect(() => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    if (user?.status === 'pending' && token) {
      pollingRef.current = setInterval(refreshUser, 30000);
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [user?.status, token, refreshUser]);

  const value: AuthContextType = useMemo(() => ({
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    logout,
  }), [user, token, isLoading, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
};
